import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {finalize, of, take, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {TranslocoDirective} from '@jsverse/transloco';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {LogInIcon, LucideAngularModule} from 'lucide-angular';
import {RouteGenerator} from '@core/route-generator';
import {LoginComponent} from '@domain/common/component/login/login.component';
import {AltchaComponent} from '@shared/altcha.component';
import {SpinnerComponent} from '@shared/spinner/spinner.component';
import {AuthService} from '@core/auth/auth.service';
import {User, UserState} from '@domain/user/user';
import {UI_ROUTES} from '@core/ui.routes';
import {ValidateEmailRequest} from '@core/auth/auth.interfaces';
import {ContactSupportComponent} from '@shared/content/contact-support/contact-support.component';

@Component({
  selector: 'app-auth-validate-user-email',
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    LucideAngularModule,
    RouterLink,
    LoginComponent,
    AltchaComponent,
    SpinnerComponent,
    ContactSupportComponent
  ],
  templateUrl: './validate-user-email.component.html',
  styleUrl: './validate-user-email.component.scss'
})
export class ValidateUserEmailComponent implements OnInit{

  private static readonly VALIDATE_EMAIL_DELAY = 36_000_000; // 10 hours
  private static readonly QUERY_PARAM_NOW = 'now';
  private static readonly QUERY_PARAM_DATA = 'data';
  private static readonly QUERY_PARAM_HASH = 'hash';
  private static readonly QUERY_PARAM_SECRET = 'secret';

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);

  public readonly captchaOk = signal(false);
  public readonly validating = signal(false);
  public readonly validationOk = signal(false);
  public readonly validationError = signal(false);
  public readonly authenticatedUser = signal<User | null>(null);
  public readonly needsValidation = computed(() => {
    const user = this.authenticatedUser();
    return !user || user.state === UserState.Registered;
  });
  public readonly isValidated = computed(() => this.captchaOk() && this.authenticatedUser()?.state === UserState.Active);
  public readonly showInvalidLink = signal(false);


  public readonly resentValidationEmail = signal(false);
  public readonly resentValidationEmailTo = signal<string|null>(null);
  public readonly showLogin = signal(false);
  public readonly showResend = computed(() => {
    return !this.resentValidationEmail() && this.needsValidation() && !!this.authenticatedUser() && !this.showLogin() && !this.showCaptcha();
  });

  public readonly showResendLogin = computed(() => {
    return this.needsValidation() && !this.authenticatedUser() && !this.showResend() && !this.showLogin() && !this.showCaptcha();
  });

  public readonly showCaptcha = computed(() => !this.captchaOk() && !this.showInvalidLink());

  public captchaForm = this.fb.group({
    captcha: ['', Validators.required]
  });

  public readonly routes = {
    login: RouteGenerator.abs(UI_ROUTES.auth.login),
    dashboard: RouteGenerator.abs(UI_ROUTES.account.dashboard),
  };

  public readonly icons = {
    login: LogInIcon
  };

  public ngOnInit(): void {
    const validationParams = this.getValidationParams();
    this.showInvalidLink.set(!validationParams);
    const user = this.authService.getAuthenticatedUser();
    this.authenticatedUser.set(user);
  }

  public validateUser(): void {
    this.captchaOk.set(true);

    const validateRequest = this.getValidationRequest();
    if (!validateRequest) {
      return;
    }
    this.validating.set(true);
    this.authService.validateEmail(validateRequest).pipe(
      take(1),
      catchError(_err => {
        this.validationError.set(true);
        return of(false);
      }),
      tap(res => {
        if (res) {
          this.validationOk.set(true)
          this.authenticatedUser.set(this.authService.getAuthenticatedUser());
        } else {
          this.validationError.set(true);
        }
      }),
      finalize(() => {
        this.validating.set(false);
      })
    ).subscribe(_ => {});
  }

  public toggleShowLogin(): void {
    this.showLogin.set(!this.showLogin());
  }

  public resendValidationEmail(user?: User): void {
    this.showLogin.set(false);
    const email = user?.email ?? this.authenticatedUser()?.email;
    if (!email) {
      return;
    }
    this.authService.resendValidationEmail({
      email: email
    }).pipe(
      catchError(_ => of(false)),
      take(1)
    ).subscribe(sent => {
      this.resentValidationEmailTo.set(email);
      this.resentValidationEmail.set(sent);
    });
  }

  private getDataParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateUserEmailComponent.QUERY_PARAM_DATA] ?? null;
  }

  private getHashParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateUserEmailComponent.QUERY_PARAM_HASH] ?? null;
  }

  private getSecretParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateUserEmailComponent.QUERY_PARAM_SECRET] ?? null;
  }

  private getStringTimeParam(): string | null {
    return this.route.snapshot.queryParams?.[ValidateUserEmailComponent.QUERY_PARAM_NOW] ?? null;
  }

  private getIntTime(): number | null {
    const time = this.getStringTimeParam();
    if (!time) {
      return null;
    }
    const num = parseInt(time, 10);
    if (isNaN(num)) {
      return null;
    }
    return num;
  }

  private isExpiredLink(): boolean {
    const t = this.getIntTime();
    if (!t) {
      return false;
    }
    return t + ValidateUserEmailComponent.VALIDATE_EMAIL_DELAY < Date.now();
  }

  private getValidationParams(): ValidateEmailRequest | null {
    const time = this.getIntTime();
    const data = this.getDataParam();
    const secret = this.getSecretParam();
    const hash = this.getHashParam();
    return (time && data && secret && hash && !this.isExpiredLink())
      ? { data: data, secret: secret, now: time.toString(10), hash: hash }
      : null;
  }

  private getValidationRequest(): ValidateEmailRequest | null {
    const captcha = this.captchaForm?.get('captcha')?.value ?? null;
    if (!captcha) {
      return null;
    }
    const params = this.getValidationParams();
    if (!params) {
      return null;
    }
    return {... params, captcha: captcha};
  }

}
